/**
 * Récupération d'un token d'authentification à usage unique déposé dans l'URL
 * (lien magique de connexion, lien d'invitation, lien d'activation).
 *
 * E1 (audit 2026-08-07) — l'API émet désormais ces liens avec le token dans le
 * FRAGMENT (`#token=…`) et non plus en query string. Un fragment n'est jamais
 * envoyé au serveur, n'apparaît pas dans l'en-tête `Referer`, et n'est pas capté
 * par le `$current_url` du premier pageview PostHog.
 *
 * La query string reste lue en repli : des liens `?token=` sont déjà partis par
 * email et restent valides (jusqu'à 7 jours pour un lien d'activation). Ce repli
 * pourra être retiré une fois ce délai écoulé après la mise en production.
 */
export function readLinkToken(): string | null {
  if (typeof window === "undefined") return null;
  const fromHash = new URLSearchParams(
    window.location.hash.replace(/^#/, ""),
  ).get("token");
  if (fromHash) return fromHash;
  return new URLSearchParams(window.location.search).get("token");
}

/**
 * Efface le token de la barre d'adresse (et de l'historique) : un rechargement
 * de page ne doit pas rejouer le lien, et l'URL ne doit pas rester copiable
 * telle quelle. À appeler dès que le token a été lu.
 *
 * NE PAS l'appeler sur `/invitations/accept`. Ce lien-là n'est pas consommé à
 * l'ouverture : il ouvre un FORMULAIRE (nom, consentement) que le visiteur peut
 * mettre plusieurs minutes à remplir, et qu'un rechargement — volontaire ou
 * subi — doit pouvoir retrouver. Effacer le token laissait alors un écran mort
 * dont le seul recours était de rouvrir l'email. Le token reste donc dans le
 * fragment, qui ne part ni au serveur, ni dans un `Referer`, ni chez PostHog ou
 * Sentry (`scrub-tokens.ts`) : ce qu'on accepte en plus, c'est sa présence dans
 * l'historique du navigateur, et rien d'autre.
 */
export function stripLinkToken(pathname: string): void {
  if (typeof window === "undefined") return;
  window.history.replaceState(null, "", pathname);
}
